import type { TFunction } from "i18next";

import type { PillTone } from "@/components/brand/Pill";
import { formatCount } from "@/lib/format";
import { tp } from "@/lib/plural";
import type { AdherenceStatus, SectionState } from "@/api/diagnostic";
import { UNDECIDED, type DecisionOrUndecided } from "./data/wricefDecisions";

/**
 * Mapas `token → {label, tone}` do vocabulário que o snapshot carrega.
 *
 * Todos são **funções que recebem `t`**, no padrão `makeStatus(t)` do
 * `AgentWorkflowDAG`: um `Record` construído em const de módulo chamaria `t` antes
 * do i18n inicializar e congelaria a copy no idioma errado.
 */

type T = (key: string) => string;

export interface TokenMeta {
  label: string;
  tone: PillTone;
}

// --------------------------------------------------------------------------
// estado da seção
// --------------------------------------------------------------------------

export function makeSectionStateMeta(t: T): Record<SectionState, TokenMeta> {
  return {
    ok: { label: t("Complete"), tone: "ok" },
    partial: { label: t("Partial"), tone: "warn" },
    empty: { label: t("No records"), tone: "muted" },
    not_collected: { label: t("Not collected"), tone: "muted" },
    truncated: { label: t("Truncated at source"), tone: "warn" },
    unauthorized: { label: t("Not authorized"), tone: "danger" },
    error: { label: t("Extraction error"), tone: "danger" },
  };
}

// --------------------------------------------------------------------------
// cobertura do gap (`coverage_type`) — vocabulário de gap_coverage.py
// --------------------------------------------------------------------------

export type CoverageType =
  | "grow_addon"
  | "compatibility_pack"
  | "separate_product"
  | "no_coverage";

export interface CoverageMeta extends TokenMeta {
  /**
   * `true` quando o preço **não** sai de um SKU GROW — a tela mostra o valor com
   * selo indicativo / "a cotar" em vez de somar como se fosse preço firme.
   */
  indicative: boolean;
  /**
   * Cor da fatia, explícita por token — mesmo motivo de {@link LevelMeta}:
   * `--primary` e `--warning` são ambos gold no tema escuro e fatias vizinhas
   * ficariam indistinguíveis num donut.
   */
  color: string;
}

export function makeCoverageTypeMeta(t: T): Record<CoverageType, CoverageMeta> {
  return {
    grow_addon: { label: t("GROW add-on"), tone: "ok", indicative: false, color: "#6d8fc4" },
    compatibility_pack: {
      label: t("Compatibility pack"),
      tone: "gold",
      indicative: true,
      color: "#ffc83d",
    },
    separate_product: {
      label: t("Separate product"),
      tone: "warn",
      indicative: true,
      color: "#f27325",
    },
    no_coverage: { label: t("No coverage"), tone: "danger", indicative: true, color: "#d4353c" },
  };
}

/**
 * As **cinco** fatias do donut de cobertura: `standard` mais os quatro
 * `coverage_type`.
 *
 * O design mostra quatro. A divergência é deliberada: dobrar `grow_addon` em
 * "padrão" apagaria a diferença que decide orçamento — `grow_addon` é o único
 * `coverage_type` com `indicative: false` (preço firme de SKU GROW), e "padrão"
 * é sem custo nenhum. Uma fatia a mais é mais honesto que a fusão.
 *
 * A chave `standard` casa com {@link STANDARD_SLICE_KEY} de `diagnosticData`.
 */
export function makeCoverageSliceMeta(t: T): Record<string, CoverageMeta> {
  return {
    standard: { label: t("Standard"), tone: "ok", indicative: false, color: "#27a155" },
    ...makeCoverageTypeMeta(t),
  };
}

// --------------------------------------------------------------------------
// disposição clean core (`disposition` / `by_disposition`)
// --------------------------------------------------------------------------

export type DispositionToken =
  | "standard_grow_equivalent"
  | "requires_extension"
  | "retire"
  | "undecided";

export function makeDispositionMeta(t: T): Record<DispositionToken, TokenMeta> {
  return {
    standard_grow_equivalent: { label: t("Standard GROW equivalent"), tone: "ok" },
    requires_extension: { label: t("Requires extension"), tone: "warn" },
    retire: { label: t("Retire"), tone: "muted" },
    undecided: { label: t("Undecided"), tone: "gold" },
  };
}

// --------------------------------------------------------------------------
// decisão do arquiteto por objeto WRICEF (`wricef_decision_effective`, #1343)
// --------------------------------------------------------------------------

/**
 * As quatro decisões do arquiteto, mais o balde de quem ainda não decidiu
 * (`_UNDECIDED`, o mesmo texto do domínio `wricef` no backend). O card do
 * Ato 1 (F5) e, depois, a seção "Decisão" do detalhe do objeto (F2) usam os
 * mesmos rótulos.
 */
export function makeDecisionMeta(t: T): Record<DecisionOrUndecided, TokenMeta> {
  return {
    in_scope: { label: t("In conversion scope"), tone: "navy" },
    client_validation: { label: t("Pending client validation"), tone: "gold" },
    out_of_scope: { label: t("Out of scope"), tone: "muted" },
    covered_by_standard: { label: t("Covered by standard"), tone: "ok" },
    [UNDECIDED]: { label: t("No decision"), tone: "muted" },
  };
}

// --------------------------------------------------------------------------
// índice de aderência ao padrão (EF-FUNC-011, §3.2)
// --------------------------------------------------------------------------

/** As cores que entram na base, na ordem da barra: verde, amarelo e vermelho. */
export const ADHERENCE_COLORS = ["standard", "extension", "gap"] as const;

/** As cinco cores da §3.2, com os tons da MOC. Tile, frame e drill-down leem daqui. */
export const ADHERENCE_BAR: Record<AdherenceStatus, string> = {
  standard: "bg-[#2e8b57]",
  extension: "bg-[#c9930a]",
  gap: "bg-[#d0466a]",
  excluded: "bg-[#8a94a6]",
  undecided: "bg-[#c3cad5]",
};

/** Os rótulos curtos de cada status, os da legenda da barra e dos chips. */
export function makeAdherenceStatusMeta(t: T): Record<AdherenceStatus, string> {
  return {
    standard: t("Standard"),
    extension: t("Standard + extension"),
    gap: t("GAP"),
    excluded: t("Out of the count"),
    undecided: t("No disposition"),
  };
}

// --------------------------------------------------------------------------
// alvo de extensibilidade (`extensibility_target` / `by_disposition_target`)
// --------------------------------------------------------------------------

export type ExtensionTargetToken = "key_user" | "side_by_side" | "developer" | "classic";

/**
 * Os quatro alvos, do mais limpo para o menos limpo, mais o balde de "ainda não
 * decidido".
 *
 * `classic` costuma faltar nas listas porque a `ref_clean_core_decisions` tem um
 * `CHECK` que só aceita três. Quem produz o dado é o Architect, que emite os
 * quatro, e o catálogo do projeto não tem `CHECK` nenhum. Deixá-lo de fora
 * esconderia justamente a técnica menos limpa das quatro.
 */
export function makeExtensionTargetMeta(
  t: T,
): Record<ExtensionTargetToken | "_UNKNOWN", TokenMeta> {
  return {
    key_user: { label: t("Key user"), tone: "ok" },
    side_by_side: { label: t("Side by side"), tone: "muted" },
    developer: { label: t("Developer"), tone: "gold" },
    classic: { label: t("Classic (on-stack)"), tone: "warn" },
    _UNKNOWN: { label: t("Target not defined"), tone: "muted" },
  };
}

// --------------------------------------------------------------------------
// entidades de estrutura organizacional (`org_structure`)
// --------------------------------------------------------------------------

export interface OrgEntityMeta {
  label: string;
  /**
   * Armadilha de leitura da **origem**, quando existe. É onde elas ficam
   * visíveis para quem lê a tela, em vez de só em comentário de código: um
   * arquiteto SAP que veja "Plantas por empresa" sem a ressalva do BWKEY conclui
   * que a árvore foi lida do ECC como configuração de empresa.
   */
  caveat: string | null;
}

/**
 * As 7 entidades de `org_structure`, cada uma com a sua ressalva de origem.
 *
 * Só a armadilha do BWKEY é visível na tela hoje (#465 tirou as outras duas —
 * VKORG repetido em `sales_orgs` e o SPRAS hardcoded de
 * `profit_centers`/`business_areas`/`divisions` — do card por decisão de
 * produto, não por perda de dado):
 *
 * - `plants[].company_code` é **BWKEY** (área de avaliação), não BUKRS.
 */
export function makeOrgEntityMeta(t: T): Record<string, OrgEntityMeta> {
  return {
    company_codes: { label: t("Company codes"), caveat: null },
    plants: {
      label: t("Plants"),
      caveat: t(
        "Grouped by valuation area (BWKEY), which is not the company code — see the hierarchy card.",
      ),
    },
    sales_orgs: {
      label: t("Sales organizations"),
      caveat: null,
    },
    purchasing_orgs: { label: t("Purchasing organizations"), caveat: null },
    profit_centers: {
      label: t("Profit centres"),
      caveat: null,
    },
    business_areas: { label: t("Business areas"), caveat: null },
    divisions: { label: t("Divisions"), caveat: null },
  };
}

/**
 * Vocabulário de estado da **sub-extração**, um nível abaixo do envelope.
 *
 * ⚠️ **Não é o mesmo mapa que {@link makeSectionStateMeta}.** `SectionState` tem
 * sete tokens (`ok|partial|empty|not_collected|truncated|unauthorized|error`);
 * este tem cinco, e os dois nomes que coincidem (`empty`, `not_collected`)
 * coincidem por significarem a mesma coisa um nível abaixo. Fundir os dois faria
 * a tela precisar de um token de seção que a entidade nunca tem (`partial` de uma
 * entidade só não existe) — e perderia `unrecognized`, que existe justamente para
 * dizer "o connector mandou um status que ninguém declarou".
 *
 * Os rótulos reusam as chaves de tradução do mapa de seção de propósito: é a
 * mesma frase para o leitor, e criar variantes daria duas traduções da mesma
 * ideia.
 */
export function makeOrgEntityStateMeta(t: T): Record<string, TokenMeta> {
  return {
    collected: { label: t("Complete"), tone: "ok" },
    empty: { label: t("No records"), tone: "muted" },
    not_collected: { label: t("Not collected"), tone: "muted" },
    error: { label: t("Extraction error"), tone: "danger" },
    unrecognized: { label: t("Status not recognized"), tone: "danger" },
  };
}

// --------------------------------------------------------------------------
// método de contagem de volumetria (`volumes[].method`)
// --------------------------------------------------------------------------

export type VolumeMethod = "fm" | "scan" | "unavailable";

export interface VolumeMethodMeta extends TokenMeta {
  caveat: string | null;
}

/**
 * Como cada tabela foi contada — e por que os dois métodos **não somam**.
 *
 * `fm` usa `EM_GET_NUMBER_OF_ENTRIES`, que conta linhas físicas da tabela **sem
 * filtrar MANDT**; `scan` varre paginado filtrando o mandante conectado. Números
 * de escopos diferentes num total único seriam somatório errado, e é por isso que
 * `counting_method: "mixed"` esconde `total_records` em favor de
 * `totals_by_method`.
 */
export function makeVolumeMethodMeta(t: T): Record<VolumeMethod, VolumeMethodMeta> {
  return {
    fm: {
      label: t("Function module"),
      tone: "ok",
      caveat: t(
        "Counted across all clients of the physical table — the function module does not filter MANDT.",
      ),
    },
    scan: { label: t("Paginated scan"), tone: "navy", caveat: null },
    unavailable: { label: t("Not counted"), tone: "muted", caveat: null },
  };
}

// --------------------------------------------------------------------------
// grau clean core (A–D)
// --------------------------------------------------------------------------

export type CleanCoreLevel = "A" | "B" | "C" | "D";

export interface LevelMeta extends TokenMeta {
  /**
   * Cor da fatia, explícita por token.
   *
   * `--primary` e `--warning` são **ambos gold** no tema escuro: duas fatias que
   * usem essas vars ficam indistinguíveis num donut. Por isso a cor vem daqui.
   */
  color: string;
}

export function makeCleanCoreLevelMeta(t: T): Record<CleanCoreLevel, LevelMeta> {
  return {
    A: { label: t("A · Released object"), tone: "ok", color: "#27a155" },
    B: { label: t("B · Classic API"), tone: "navy", color: "#1d4ea0" },
    C: { label: t("C · No release contract"), tone: "warn", color: "#f27325" },
    D: { label: t("D · No API"), tone: "danger", color: "#d4353c" },
  };
}

// --------------------------------------------------------------------------
// confiança da regra de ativação (`items[].confidence`)
// --------------------------------------------------------------------------

/**
 * ⚠️ **Duas grafias de `media`, as duas reais.** `_CONFIDENCE_ORDER`
 * (`engine.py:22`) aceita `media` e `média`, porque as linhas do
 * `ref_scope_activation_rules` carregam as duas. Mapear só uma jogaria metade dos
 * itens no `—` sem que ninguém percebesse.
 *
 * O motor também emite `""` para item sem regra com confiança declarada; esse
 * caso não entra no mapa — `text()` já o transforma em `undefined`, e a aba mostra
 * `—`, que é o que "não declarado" significa.
 */
export function makeConfidenceMeta(t: T): Record<string, TokenMeta> {
  const media: TokenMeta = { label: t("Medium confidence"), tone: "gold" };
  return {
    alta: { label: t("High confidence"), tone: "ok" },
    media,
    "média": media,
    baixa: { label: t("Low confidence"), tone: "warn" },
  };
}

// --------------------------------------------------------------------------
// tier da regra (`items[].tier`)
// --------------------------------------------------------------------------

/**
 * `premium` **domina** na agregação (`engine.py:196-198`): basta uma regra premium
 * para o item inteiro subir. O rótulo diz isso — não é uma média, é o teto.
 */
export function makeTierMeta(t: T): Record<string, TokenMeta> {
  return {
    base: { label: t("Base tier"), tone: "muted" },
    premium: { label: t("Premium tier"), tone: "navy" },
  };
}

// --------------------------------------------------------------------------
// qualidade da evidência (`items[].evidence_quality`) — derivado do DIAG.8
// --------------------------------------------------------------------------

/**
 * Da evidência mais forte para a mais fraca.
 *
 * `catalog_only` é `muted` de propósito: significa que **nada** foi observado no
 * sistema do cliente — nem uso, nem tabela — e o item entrou por catálogo. Dar-lhe
 * tom de sucesso apresentaria suposição como medição.
 */
export function makeEvidenceQualityMeta(t: T): Record<string, TokenMeta> {
  return {
    usage_observed: { label: t("Usage observed"), tone: "ok" },
    fingerprint_only: { label: t("Tables observed"), tone: "gold" },
    catalog_only: { label: t("Catalog only"), tone: "muted" },
  };
}

// --------------------------------------------------------------------------
// origem dos tcodes observados (`items[].observed_tcodes_source`)
// --------------------------------------------------------------------------

export interface ObservedSourceMeta extends TokenMeta {
  /** O que a lista vazia significa **neste** token. Nunca "o cliente não usa". */
  explanation: string;
}

/**
 * Os três motivos pelos quais `observed_tcodes` tem o conteúdo que tem.
 *
 * ⚠️ **Nenhum dos três diz "não usa".** `no_key_tcodes` é item do tier RAG, que
 * nasce com `key_tcodes: ""` (`map_scope.py:122`) e portanto nunca teve o que
 * cruzar; `no_usage` é a run que não trouxe classificação de uso. Só
 * `classification_usage` mediu alguma coisa — e mesmo aí, lista vazia seria
 * "cruzou e não achou", que ainda não é "não usa".
 *
 * A **ausência** da chave é um quarto estado, e não mora aqui: é run anterior ao
 * DIAG.8, e quem a trata é a aba, sobre `observedTcodes === undefined`.
 */
export function makeObservedSourceMeta(t: T): Record<string, ObservedSourceMeta> {
  return {
    classification_usage: {
      label: t("Cross-checked against recorded usage"),
      tone: "ok",
      explanation: t("Catalog transactions for this item were compared with recorded usage."),
    },
    no_usage: {
      label: t("No usage recorded in this run"),
      tone: "gold",
      explanation: t("This run brought no usage data to compare against — not the same as unused."),
    },
    no_key_tcodes: {
      label: t("No catalog transactions to cross"),
      tone: "muted",
      explanation: t("This scope item has no catalog transactions, so there was nothing to compare."),
    },
  };
}

// --------------------------------------------------------------------------
// investimento (DIAG.7) — WRICEF, perfil e senioridade
// --------------------------------------------------------------------------

/**
 * As 6 letras do acrônimo mais `"?"` — o bucket que `effort_estimator.py:336`
 * emite para objeto sem `wricef_type`. Descartá-lo faria a barra empilhada
 * deixar de fechar contra `total_effort_days` (Correção 9 do S-066).
 */
export function makeWricefTypeMeta(t: T): Record<string, TokenMeta> {
  return {
    W: { label: t("Workflow"), tone: "navy" },
    R: { label: t("Report"), tone: "navy" },
    I: { label: t("Interface"), tone: "navy" },
    C: { label: t("Conversion"), tone: "navy" },
    E: { label: t("Enhancement"), tone: "navy" },
    F: { label: t("Form"), tone: "navy" },
    "?": { label: t("Type not reported"), tone: "muted" },
    // #920: os dois silêncios da letra, e eles não são o mesmo.
    //
    // `_NOT_APPLICABLE` é o Guia de objetos dizendo que o tipo não é entregável
    // por si: classe, grupo de função e interface são parte de um entregável.
    // `_UNKNOWN` é o pipeline não ter decidido. Antes os dois viravam `E`, e o
    // card afirmava Enhancement em metade do catálogo.
    _NOT_APPLICABLE: { label: t("Not a RICEFW deliverable"), tone: "muted" },
    _UNKNOWN: { label: t("Letter not decided"), tone: "muted" },
  };
}

/** O vocabulário real de `by_profile_seniority` — mesmo split da `EffortTab`
 * do Architect, superfície diferente (frontend/src/pages/agents/Architect.tsx). */
export function makeProfileMeta(t: T): Record<string, string> {
  return {
    abap_dev: t("ABAP Dev"),
    functional: t("Functional"),
    basis: t("Basis"),
    pm: t("PM"),
    architect: t("Architect"),
    qa: t("QA"),
  };
}

export function makeSeniorityMeta(t: T): Record<string, string> {
  return {
    junior: t("Junior"),
    pleno: t("Mid-level"),
    senior: t("Senior"),
    principal: t("Principal"),
  };
}

/**
 * `"abap_dev_pleno"` → `"ABAP Dev · Mid-level"`.
 *
 * Split no **último** `_` para acomodar perfis com underscore (`abap_dev`).
 * Token fora do mapa devolve o próprio token, nunca `"?"` — um perfil novo no
 * rate card não pode desaparecer da tabela.
 */
export function formatProfileSeniority(key: string, t: T): string {
  const lastUnderscore = key.lastIndexOf("_");
  if (lastUnderscore === -1) return key;

  const profile = key.slice(0, lastUnderscore);
  const seniority = key.slice(lastUnderscore + 1);
  const profileLabel = makeProfileMeta(t)[profile] ?? profile;
  const seniorityLabel = makeSeniorityMeta(t)[seniority] ?? seniority;
  return `${profileLabel} · ${seniorityLabel}`;
}

/**
 * Nome de negócio de cada alavanca de otimização (S-080).
 *
 * Texto fixo por `case` e não mapa indexado: o guarda de i18n não enxerga
 * chamada de tradução com variável, e as cinco saíam cruas numa tela em
 * português — já aconteceu uma vez, na #682.
 *
 * Vive aqui porque os dois atos falam das mesmas alavancas: o Ato 3 conta
 * quanta gente cada uma move, o Ato 5 mostra quanto ela vale. Duas cópias
 * divergiriam na primeira mudança de vocabulário.
 */
export function leverLabel(lever: string, t: (key: string) => string): string {
  switch (lever) {
    case "tier_downgrade":
      return t("Reclassified by actual use");
    case "combo_consolidation":
      return t("Consolidated into the combo SKU");
    case "block_fill":
      return t("Paid block becomes room to grow");
    case "self_service":
      return t("Moved to self-service");
    default:
      return t("Other reclassification");
  }
}

/**
 * A alavanca como **frase contada**, para a prova do Ato 3.
 *
 * Diferente do {@link leverLabel}, que é sintagma nominal para a prosa do Ato
 * 5 ("reclassificação pelo uso real"). Prefixar número a ele não forma frase:
 * saía "54 reclassificação pelo uso real" e "7 bloco pago vira espaço". O
 * desenho escreve cada item da lista como oração inteira, com o número dentro.
 *
 * ⚠️ Só `tier_downgrade` e `combo_consolidation` são alcançáveis pelo único
 * chamador: o agregado `users_by_lever` nasce de `movements`, e um movimento só
 * carrega essas duas (`license_scenario.py:457,500`). `block_fill` é rótulo de
 * **linha** e `self_service` não é atribuída em lugar nenhum. Os dois ficam
 * porque são a frase certa no dia em que o motor os atribuir a movimento — o
 * `default` diria "reclassificados pelo uso real", que é falso para bloco pago.
 */
export function leverFinding(lever: string, users: number, t: TFunction): string {
  /*
    As duas formas escritas à mão, pelo `tp`, que é como este app faz plural
    (`lib/plural.ts` explica por que não é o plural nativo do i18next: chave
    com sufixo some do guarda de cobertura).

    Até 02/10/2026 a contagem chegava aqui **já formatada**, em string, e não
    havia plural nenhum: o card do cliente dizia "1 usuários se consolidam num
    SKU combinado". Agora entra o número, e o texto exibido vai em
    `formatted` — com `{{count}}` na frase perderíamos o separador de milhar,
    e a mesma linha chega a ter centenas de usuários.
  */
  const vars = { formatted: formatCount(users) };
  switch (lever) {
    case "tier_downgrade":
      return tp(
        t,
        users,
        "{{formatted}} user moves to the licence their use sustains",
        "{{formatted}} users move to the licence their use sustains",
        vars,
      );
    case "combo_consolidation":
      return tp(
        t,
        users,
        "{{formatted}} user consolidates into a single combined SKU",
        "{{formatted}} users consolidate into a single combined SKU",
        vars,
      );
    case "block_fill":
      return tp(
        t,
        users,
        "{{formatted}} already-paid seat becomes room to grow",
        "{{formatted}} already-paid seats become room to grow",
        vars,
      );
    case "self_service":
      return tp(
        t,
        users,
        "{{formatted}} user moves to self-service",
        "{{formatted}} users move to self-service",
        vars,
      );
    default:
      return tp(
        t,
        users,
        "{{formatted}} user is reclassified by their actual use",
        "{{formatted}} users are reclassified by their actual use",
        vars,
      );
  }
}

// --------------------------------------------------------------------------
// motivo do gap (`gap_reasons`)
// --------------------------------------------------------------------------

export type GapReasonToken =
  | "not_in_grow"
  | "migration_disposition"
  | "hcm_no_grow_license";

/**
 * Por que um scope item virou gap, em palavra de gente.
 *
 * O vocabulário é fechado e tem três entradas, todas em `gap_coverage.py`. A
 * tela imprimia o token cru, e em 02/10/2026 o único gap do Ato 3 exibia
 * `migration_disposition` debaixo do nome do item — o nome de uma coluna, na
 * tela que o cliente lê.
 *
 * Token novo no backend continua aparecendo como ele é: sumir com um motivo
 * que a tela não conhece seria pior que mostrá-lo feio, porque o gap ficaria
 * sem explicação nenhuma. É o mesmo princípio do `appl` sem rótulo.
 */
export function makeGapReasonMeta(t: T): Record<GapReasonToken, string> {
  return {
    not_in_grow: t("Not in the GROW catalogue"),
    migration_disposition: t("Has a simplification item in the migration"),
    hcm_no_grow_license: t("HR has no licence in GROW"),
  };
}

export function gapReasonLabel(token: string, t: T): string {
  return makeGapReasonMeta(t)[token as GapReasonToken] ?? token;
}
